export const MIN_PLANNER_STATISTICS_WIDTH = 160

export type GrossBoardColumnWidths = {
    order: number
    driver: number
    truck: number
    status: number
    dispatchStatus: number
    dispatcher: number
    dispatchNote: number
    location: number
    number: number
    day: number
    totalMileage: number
    brokerRate: number
    driverRate: number
    rpm: number
    statistics: number
}

export const DEFAULT_GROSS_BOARD_WIDTHS: GrossBoardColumnWidths = {
    order: 48,
    driver: 160,
    truck: 176,
    status: 144,
    // Wider than a one-word chip: dispatch labels are free text and often spell out a transition
    // ("Enroute -> Ready").
    dispatchStatus: 156,
    dispatcher: 120,
    dispatchNote: 144,
    location: 130,
    number: 48,
    day: 78,
    totalMileage: 104,
    brokerRate: 128,
    driverRate: 96,
    rpm: 82,
    statistics: 180,
}

export type GrossBoardLeftColumnKey = 'driver' | 'truck' | 'status' | 'dispatchStatus' | 'dispatcher' | 'dispatchNote' | 'location' | 'number'
export type GrossBoardRightColumnKey = 'totalMileage' | 'brokerRate' | 'driverRate' | 'rpm' | 'statistics' | 'dispatcher' | 'dispatchNote'
export type GrossBoardColumnKey = GrossBoardLeftColumnKey | GrossBoardRightColumnKey

export const DEFAULT_GROSS_BOARD_LEFT_ORDER: GrossBoardLeftColumnKey[] = [
    'driver',
    'truck',
    'dispatchStatus',
    'status',
    'location',
    'dispatcher',
    'dispatchNote',
]
export const DEFAULT_GROSS_BOARD_RIGHT_ORDER: GrossBoardRightColumnKey[] = ['statistics']

const isGrossBoardRightColumnKey = (value: string): value is GrossBoardRightColumnKey => DEFAULT_GROSS_BOARD_RIGHT_ORDER.some((key) => key === value)

export const DEFAULT_GROSS_BOARD_COLUMN_LABELS: Record<GrossBoardColumnKey, string> = {
    driver: 'Driver',
    truck: 'Truck / Trailer',
    status: 'Driver Status',
    dispatchStatus: 'Dispatch Status',
    dispatcher: 'Dispatcher',
    dispatchNote: 'Notes',
    location: 'Location',
    number: 'N⁰',
    totalMileage: 'Total Mileage',
    brokerRate: 'Original Rate',
    driverRate: 'Driver Rate',
    rpm: 'RPM',
    statistics: 'Statistics',
}

/**
 * Keeps saved right-column layouts valid when columns are added or removed.
 *
 * The removed Gross column is intentionally ignored when old preferences are loaded, so it cannot
 * reappear after a refresh or through the column settings modal.
 */
export const normalizeGrossRightOrder = (
    value: unknown,
    fallback: GrossBoardRightColumnKey[] = DEFAULT_GROSS_BOARD_RIGHT_ORDER,
    removed: GrossBoardColumnKey[] = []
): GrossBoardRightColumnKey[] => {
    if (!Array.isArray(value)) return fallback

    const next: GrossBoardRightColumnKey[] = []
    value.forEach((item) => {
        if (typeof item !== 'string') return
        if (!isGrossBoardRightColumnKey(item) || next.includes(item)) return
        next.push(item)
    })

    if (!next.includes('statistics') && !removed.includes('statistics')) next.unshift('statistics')

    return next
}

export type GrossBoardGridOptions = {
    leftViewportWidth?: number
    leftOrder?: GrossBoardLeftColumnKey[]
    rightOrder?: GrossBoardRightColumnKey[]
    showDriverRate?: boolean
    collapsedColumns?: GrossBoardColumnKey[]
}

const COLLAPSED_GROSS_BOARD_COLUMN_WIDTH = 34

/**
 * Returns the Gross Board week view grid template for a given number of day columns.
 * Left info columns + week timeline + right financial columns.
 * regardless of how many day columns are shown.
 */
export const getGrossBoardGrid = (
    numDays: number,
    widths: GrossBoardColumnWidths = DEFAULT_GROSS_BOARD_WIDTHS,
    options: GrossBoardGridOptions = {},
    widthResolver?: (key: keyof GrossBoardColumnWidths, width: number) => string
): string => {
    const resolveWidth = (key: keyof GrossBoardColumnWidths) => {
        const width = widths[key]
        return widthResolver ? widthResolver(key, width) : `${width}px`
    }

    const leftOrder = options.leftOrder ?? DEFAULT_GROSS_BOARD_LEFT_ORDER
    const rightOrder = options.rightOrder ?? DEFAULT_GROSS_BOARD_RIGHT_ORDER
    const showDriverRate = options.showDriverRate ?? true
    const collapsedColumns = new Set(options.collapsedColumns ?? [])

    const leftCols = leftOrder.map((key) => (collapsedColumns.has(key) ? `${COLLAPSED_GROSS_BOARD_COLUMN_WIDTH}px` : resolveWidth(key))).join(' ')
    const rightKeys = rightOrder.filter((key) => key !== 'driverRate' || showDriverRate)
    const rightCols = rightKeys.map((key) => (collapsedColumns.has(key) ? `${COLLAPSED_GROSS_BOARD_COLUMN_WIDTH}px` : resolveWidth(key))).join(' ')

    if (options.leftViewportWidth !== undefined)
        return `var(--planner-left-width, ${options.leftViewportWidth}px) repeat(${numDays}, minmax(0, 1fr)) ${rightCols}`

    return `${resolveWidth('order')} ${leftCols} repeat(${numDays}, minmax(0, 1fr)) ${rightCols}`
}
